import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

function useVisible(threshold = 0.1) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { visible, ref };
}

const cases = [
  {
    brand: '蒙牛',
    tag: '案例一',
    logo: '/images/partner-mengniu.jpeg',
    highlight: '全年费用大幅度降低，品牌真正"看清渠道、摸透终端、连接消费者"',
    badge: '促动销 · 管分销 · 提费效',
    colorClass: 'bg-blue-600',
    lightClass: 'bg-blue-50',
    borderClass: 'border-blue-100 hover:border-blue-300',
    textClass: 'text-blue-700',
    bannerBg: 'bg-blue-600',
  },
  {
    brand: '康师傅',
    tag: '案例二',
    logo: '/images/partner-kangshifu.jpeg',
    highlight: '五码合一全链路数字化，从生产端到消费者数据闭环',
    badge: '链路透明 · 动销可追踪 · 费用有归处',
    colorClass: 'bg-orange-500',
    lightClass: 'bg-orange-50',
    borderClass: 'border-orange-100 hover:border-orange-300',
    textClass: 'text-orange-700',
    bannerBg: 'bg-orange-500',
  },
  {
    brand: '王老吉',
    tag: '案例三',
    logo: '/images/partner-wanglaoji.jpeg',
    highlight: '百亿品牌从零建链路，费用不再层层截留，终端更愿意卖',
    badge: 'bC联动 · 截留破解 · 终端激活',
    colorClass: 'bg-red-600',
    lightClass: 'bg-red-50',
    borderClass: 'border-red-100 hover:border-red-300',
    textClass: 'text-red-700',
    bannerBg: 'bg-red-600',
  },
];

export default function SuccessCases() {
  const headerRef = useVisible(0.05);
  const cardsRef = useVisible(0.05);

  return (
    <section className="py-16 md:py-24 bg-surface-warm">
      <div className="w-full px-4 md:px-8 lg:px-12">
        {/* Section Header */}
        <div ref={headerRef.ref} className={`text-center mb-10 md:mb-14 transition-all duration-700 ${headerRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-primary mb-3">
            标杆案例
          </h2>
          <p className="text-sm md:text-base text-text-muted max-w-2xl mx-auto leading-relaxed">
            来自蒙牛、康师傅、王老吉等头部品牌的一线实战经验
          </p>
        </div>

        {/* Case Cards */}
        <div ref={cardsRef.ref} className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {cases.map((c, i) => (
            <Link
              key={i}
              to="/cases"
              className={`group block bg-white rounded-xl border ${c.borderClass} p-5 md:p-6 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 ${cardsRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              style={{ transitionDelay: `${150 + i * 120}ms` }}
            >
              {/* Top bar */}
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-white rounded-lg border border-gray-100 overflow-hidden shrink-0">
                    <img
                      src={c.logo}
                      alt={`${c.brand} logo`}
                      className="w-full h-full object-contain p-0.5"
                      loading="lazy"
                    />
                  </div>
                  <span className="text-lg font-bold text-primary">{c.brand}</span>
                </div>
              </div>

              {/* Highlight */}
              <p className="text-sm text-text-secondary leading-relaxed mb-4 group-hover:text-primary transition-colors">
                {c.highlight}
              </p>

              {/* Badge */}
              <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 ${c.lightClass} ${c.textClass} text-xs font-medium rounded-lg`}>
                <i className="ri-shining-line" />
                {c.badge}
              </div>

              {/* Bottom link */}
              <div className="mt-5 pt-4 border-t border-gray-50 flex items-center gap-1 text-xs text-text-muted group-hover:text-accent transition-colors">
                <span>查看完整案例</span>
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>

        {/* View All CTA */}
        <div className={`text-center mt-10 md:mt-12 transition-all duration-700 delay-500 ${cardsRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <Link
            to="/cases"
            className="inline-flex items-center gap-2 px-6 py-3 border border-primary/20 text-primary text-sm font-semibold rounded-lg hover:bg-primary hover:text-white transition-all duration-300"
          >
            <i className="ri-file-list-3-line" />
            查看全部案例详情
          </Link>
        </div>
      </div>
    </section>
  );
}